/**
 * HealthDashboardPage
 *
 * Feature: 017-backup-infrastructure-setup · REBUILT by Spec 216 (T025/FR-018)
 * Constitution: v1.23.0
 *
 * Infrastructure Health = the collected reachability + inventory status of the
 * Azure estate FabricControlPlane manages, displayed from the DATABASE. The
 * old "Run Health Check" button (webapp-side Azure SDK probes) is deleted —
 * the webapp never probes Azure; gaps render as explicit "not collected"
 * (they are collection requests, not numbers to fake).
 *
 * How this differs from Platform Health: Platform Health asks "is OUR
 * platform (containers/jobs/registry/Logic Apps) running?"; this screen asks
 * "can the platform reach and see the ESTATE it manages (KV, SQL MI, Fabric
 * API, storage accounts)?"
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Activity, RefreshCw } from 'lucide-react';
import { useInfrastructureHealth } from '../hooks/useInfrastructure';
import { HealthHistoryTable } from '../components/HealthHistoryTable';
import type { CollectedSignal, CollectedHealthComponent } from '../types';
import {
  PageLayout,
  Card,
  StatCard,
  Button,
  Badge,
  ErrorState,
  LoadingState,
  NotCollected,
  Extended,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import type { BadgeVariant } from '../../../components/ui';
import { infrastructureHelp } from '../../../data/help/infrastructureHelp';
import { formatEasternDate } from '@/utils/dateFormatter';

const SIGNAL_VARIANT: Record<CollectedSignal, BadgeVariant> = {
  ok: 'success',
  attention: 'error',
  stale: 'warning',
  'not-collected': 'secondary',
};

const SIGNAL_LABEL: Record<CollectedSignal, string> = {
  ok: 'OK',
  attention: 'Attention',
  stale: 'Stale',
  'not-collected': 'Not collected',
};

const BANNER_TONE: Record<CollectedSignal, string> = {
  ok: 'border-[var(--color-status-success)]/40 bg-[var(--color-status-success)]/10 text-[var(--color-status-success)]',
  attention: 'border-[var(--color-status-danger)]/40 bg-[var(--color-status-danger)]/10 text-[var(--color-status-danger)]',
  stale: 'border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/10 text-[var(--color-status-warning)]',
  'not-collected': 'border-[var(--color-border)] bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]',
};

const BANNER_TEXT: Record<CollectedSignal, string> = {
  ok: 'All collected signals healthy',
  attention: 'A collected signal needs attention',
  stale: 'Collected signals are stale (older than 24h)',
  'not-collected': 'No health signals collected yet',
};

const formatRelativeTime = (iso?: string | null): string => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const diffMins = Math.floor((Date.now() - then) / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  return formatEasternDate(iso);
};

/** Friendly names for the subscription GUIDs we manage; falls back to the raw id. */
const SUBSCRIPTION_NAMES: Record<string, string> = {
  '10909d93-6bd3-4ebe-88a9-0b4008fbeaa1': 'Dobler Data',
  'a4fd8395-a884-4caf-a222-cd978ce20d61': 'Azure DevTest subscription 1',
};

const subscriptionName = (id?: string | null): string => {
  if (!id) return '—';
  return SUBSCRIPTION_NAMES[id.toLowerCase()] ?? `${id.slice(0, 8)}…`;
};

const keyVaultName = (url?: string | null): string => {
  if (!url) return '—';
  try {
    return new URL(url).hostname.split('.')[0] || url;
  } catch {
    return url;
  }
};

/** One collected component card: signal + criterion + collection timestamp. */
const ComponentCard: React.FC<{ component: CollectedHealthComponent }> = ({ component }) => (
  <Card>
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-medium text-[var(--color-text-primary)]">{component.component}</p>
      {component.signal === 'not-collected' ? (
        <NotCollected reason={component.message ?? component.criterion} />
      ) : (
        <AppTooltip title={component.criterion}>
          <span>
            <Badge variant={SIGNAL_VARIANT[component.signal]} isDot isPill>
              {SIGNAL_LABEL[component.signal]}
            </Badge>
          </span>
        </AppTooltip>
      )}
    </div>
    <div className="mt-2 space-y-1 text-xs text-[var(--color-text-secondary)]">
      {component.message && component.signal !== 'not-collected' && <p>{component.message}</p>}
      {component.latencyMs != null && <p>Latency: {component.latencyMs} ms</p>}
      <p>
        Collected: {formatRelativeTime(component.lastCollectedAt)}
      </p>
    </div>
  </Card>
);

export const HealthDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');

  const { data: health, isLoading, error, refetch } = useInfrastructureHealth(true, 30000);

  const tabs: { key: 'overview' | 'history'; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'history', label: 'History' },
  ];

  return (
    <PageLayout
      title="Infrastructure Health"
      description="Collected reachability + inventory status of the Azure estate we manage — displayed from the database, never probed live"
      icon={<Activity className="h-6 w-6" />}
      actions={
        <>
          <PageHelpPanel content={infrastructureHelp} buttonSize="sm" />
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            Refresh
          </Button>
        </>
      }
    >
      {error ? (
        <ErrorState
          title="Failed to Load Health Status"
          message={error.message}
          onRetry={() => refetch()}
        />
      ) : isLoading && !health ? (
        <LoadingState rows={6} />
      ) : (
        <>
          {/* Overall banner */}
          {health && (
            <div className={clsx('mb-6 rounded-lg border p-4', BANNER_TONE[health.overall])}>
              <p className="font-medium">{BANNER_TEXT[health.overall]}</p>
              <p className="text-sm opacity-75">
                Signals are written by the collection job each run — the newest is shown below per component.
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6 flex gap-1 border-b border-[var(--color-border)]">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                  activeTab === tab.key
                    ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                    : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Collected component cards */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {(health?.components ?? []).map((component) => (
                  <ComponentCard key={component.component} component={component} />
                ))}
              </div>

              <Extended>
                {/* Environment context — plain-language replacement for the old
                    confusing "Environment Details" block */}
                <Card padding="lg">
                  <h3 className="mb-1 text-lg font-medium text-[var(--color-text-primary)]">
                    Environment Context
                  </h3>
                  <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
                    The Azure resources this environment is configured against.
                  </p>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <StatCard label="Environment" value={health?.environment ?? '—'} />
                    <StatCard
                      label="Key Vault"
                      value={
                        <span className="block truncate" title={health?.resources?.keyVaultUrl ?? undefined}>
                          {keyVaultName(health?.resources?.keyVaultUrl)}
                        </span>
                      }
                    />
                    <StatCard
                      label="Subscription / RG"
                      value={<span className="block truncate">{subscriptionName(health?.resources?.subscriptionId)}</span>}
                      subtext={
                        <span className="block truncate" title={health?.resources?.resourceGroup ?? undefined}>
                          {health?.resources?.resourceGroup ?? '—'}
                        </span>
                      }
                    />
                    <StatCard label="Region" value={health?.resources?.region ?? '—'} />
                  </div>
                </Card>
              </Extended>
            </div>
          )}

          {activeTab === 'history' && <HealthHistoryTable />}
        </>
      )}
    </PageLayout>
  );
};

export default HealthDashboardPage;
